<template>
  <div
    :class="`author-widget-${props.theme}`"
    :data-testid="dataTestid"
  >
    <atoms-media-objects
      :align="
        props.theme === 'secondary'
          ? 'center'
          : 'top'
      "
    >
      <template #image>
        <nuxt-link :to="AUTHOR_URL">
          <NuxtImg
            loading="lazy"
            width="96"
            height="96"
            class="w-20 min-w-[80px] lg:min-w-[96px] h-20 lg:w-24 lg:h-24 rounded-full object-cover"
            :src="`${props.authorImage}?w=96&h=96`"
            :alt="`author ${props.authorName}`"
            format="webp"
          />
        </nuxt-link>
      </template>

      <div class="ml-1 lg:ml-3">
        <div class="mb-2">
          <div class="flex flex-wrap items-center">
            <p ref="authorName">
              <nuxt-link
                :to="AUTHOR_URL"
                :class="classes[props.theme].name"
                class="mr-3"
              >
                <strong>{{ pageData.firstName }} {{ pageData.lastName }}</strong>
              </nuxt-link>
            </p>

            <MoleculesShareButtons
              v-if="props.theme === 'default'"
              :size="devices.isMobile.value ? 'xxsmall' : 'small'"
              :buttons="props.socialMedia"
            />
          </div>

          <p
            v-if="props.jobTitle"
            ref="jobTitle"
            :class="classes[props.theme].jobTitle"
          >
            {{ props.jobTitle }}
          </p>
        </div>

        <div
          v-if="props.theme === 'default'"
          class="text-xs lg:text-sm"
        >
          <slot />
        </div>

        <nuxt-link
          v-if="props.theme === 'default'"
          :to="AUTHOR_URL"
          class="text-semantic-sizing-label-lg text-semantic-primary-default underline font-bold inline-flex items-center mt-2.5"
        >
          More from author

          <V2IconsArrow
            direction="right"
            class="fill-semantic-primary-default ml-2"
            width="20"
            height="20"
          />
        </nuxt-link>
      </div>
    </atoms-media-objects>

    <div
      v-if="props.theme === 'secondary'"
      class="text-base mt-3"
    >
      <slot />
    </div>

    <MoleculesShareButtons
      v-if="props.theme === 'secondary'"
      size="medium"
      class="mt-5"
      :buttons="socialMedia"
    />
  </div>
</template>

<script setup>
import {
  arrayObjectPropValidator
} from '~/helpers/validator'

import {
  DEFAULT_AUTHOR_URL
} from '~/constants/pages/contentful'

defineOptions({
  name: 'V2OrganismAuthorWidget'
})

const { devices } = deviceChecker()

const props = defineProps({
  dataTestid: {
    type: String,
    default: 'author-widget'
  },

  authorImage: {
    type: String,
    default: FEATURED_IMAGE_DEFAULT_IMAGE
  },

  theme: {
    type: String,
    default: 'default',
    validator: value => ['default', 'secondary'].includes(value)
  },

  authorName: {
    type: String,
    default: 'Name'
  },

  authorUrl: {
    type: String,
    default: '/compare-club'
  },

  jobTitle: {
    type: String,
    default: 'Job Title'
  },

  socialMedia: {
    type: Array,
    default: () => [
      {
        name: 'Email',
        url: 'mailto:someone@example.com'
      }
    ],
    validator: arr => {
      return arrayObjectPropValidator(arr, ['name', 'url'])
    }
  }
})

const classes = {
  default: {
    name: 'text-semantic-sizing-body-sm underline',
    jobTitle: 'text-xs lg:text-sm font-bold'
  },
  secondary: {
    name: 'text-2xl lg:text-2.5xl font-black',
    jobTitle: 'text-sm lg:text-base font-bold'
  }
}

const pageData = computed(() => {
  const firstName = (() => props.authorName.split(' ')[0])()
  const lastName = (() => props.authorName.replace(firstName, ''))()

  return {
    firstName,
    lastName
  }
})

const AUTHOR_URL = computed(() => {
  const url = props.authorUrl

  if (!url) {
    return url
  }

  return trailingSlash(`${DEFAULT_AUTHOR_URL}${url}`)
})
</script>
